import React, { useMemo, useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth } from 'date-fns';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { fmtDate, parseLocalDate } from '../lib/format';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function fmtBlockoutRange(b) {
  if (!b) return '';
  if (b.start_date === b.end_date) return fmtDate(b.start_date, 'EEE, MMM d');
  return `${fmtDate(b.start_date, 'MMM d')} – ${fmtDate(b.end_date, 'MMM d')}`;
}

// Block-out calendar (Section 6 Phase 5): a month grid, 7 columns, service days
// outlined, blocked days filled; overlapping blockouts simply read as one run
// of filled days (B-04). Tap a day to start a range and another to end it (one
// tap twice = a single day), add an optional note, then "Block out". Past
// dates are allowed (B-05). The list under the grid can remove each one, after
// the PC6 confirm (Fix 6F; wherever the calendar renders, reviewer addendum A2).
export default function BlockoutCalendar({
  blockouts, serviceDates, today, onAdd, onDelete, busy = false, intro = null, testIdPrefix = 'blockout',
}) {
  const todayDate = parseLocalDate(today) || new Date();
  const [month, setMonth] = useState(() => startOfMonth(todayDate));
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [note, setNote] = useState('');
  const [pendingRemove, setPendingRemove] = useState(null);

  const blocked = useMemo(() => {
    const set = new Set();
    (blockouts || []).forEach((b) => {
      let d = parseLocalDate(b.start_date);
      const last = parseLocalDate(b.end_date);
      let guard = 0;
      while (d && last && d <= last && guard < 800) {
        set.add(format(d, 'yyyy-MM-dd'));
        d = addDays(d, 1);
        guard += 1;
      }
    });
    return set;
  }, [blockouts]);
  const services = useMemo(() => new Set(serviceDates || []), [serviceDates]);

  const days = useMemo(() => {
    const out = [];
    let d = startOfWeek(startOfMonth(month));
    const last = endOfWeek(endOfMonth(month));
    while (d <= last) {
      out.push(d);
      d = addDays(d, 1);
    }
    return out;
  }, [month]);

  const lo = start && end ? (start <= end ? start : end) : start;
  const hi = start && end ? (start <= end ? end : start) : start;

  const pick = (ymd) => {
    if (!start || (start && end)) { setStart(ymd); setEnd(null); return; }
    setEnd(ymd);
  };
  const clear = () => { setStart(null); setEnd(null); setNote(''); };
  const submit = async () => {
    if (!lo) return;
    const ok = await onAdd({ start_date: lo, end_date: hi || lo, note: note.trim() || null });
    if (ok !== false) clear();
  };

  const todayYmd = format(todayDate, 'yyyy-MM-dd');
  const sorted = [...(blockouts || [])].sort((a, b) => (a.start_date < b.start_date ? -1 : 1));

  return (
    <div data-testid={`${testIdPrefix}-calendar`}>
      {intro && <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{intro}</p>}
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth((m) => addMonths(m, -1))} aria-label="Previous month" data-testid={`${testIdPrefix}-prev-month`}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100" data-testid={`${testIdPrefix}-month`}>{format(month, 'MMMM yyyy')}</span>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth((m) => addMonths(m, 1))} aria-label="Next month" data-testid={`${testIdPrefix}-next-month`}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => <span key={i} className="text-xs text-slate-500 dark:text-slate-400 py-1">{w}</span>)}
        {days.map((d) => {
          const ymd = format(d, 'yyyy-MM-dd');
          const inMonth = isSameMonth(d, month);
          const isBlocked = blocked.has(ymd);
          const selected = lo && ymd >= lo && ymd <= (hi || lo);
          const isService = services.has(ymd);
          let cls = 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5';
          if (isBlocked) cls = 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300';
          if (selected) cls = 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300';
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => pick(ymd)}
              className={`h-9 rounded-lg text-sm transition-colors ${cls} ${isService ? 'ring-1 ring-inset ring-teal-500' : ''} ${inMonth ? '' : 'opacity-40'} ${ymd === todayYmd ? 'font-bold' : ''}`}
              aria-label={`${format(d, 'EEEE, MMMM d')}${isBlocked ? ', blocked out' : ''}${isService ? ', service day' : ''}`}
              data-testid={`${testIdPrefix}-day-${ymd}`}
              data-blocked={isBlocked ? 'true' : undefined}
              data-service={isService ? 'true' : undefined}
            >
              {format(d, 'd')}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded ring-1 ring-inset ring-teal-500" /> Service</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 dark:bg-red-500/15" /> Blocked out</span>
      </div>

      {lo && (
        <div className="mt-3 rounded-lg bg-slate-50 dark:bg-[#34373c] px-3 py-3 space-y-2" data-testid={`${testIdPrefix}-form`}>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100" data-testid={`${testIdPrefix}-form-range`}>
            {fmtBlockoutRange({ start_date: lo, end_date: hi || lo })}
            {!end && <span className="font-normal text-slate-500 dark:text-slate-400"> · tap another day for a range</span>}
          </p>
          <Input value={note} onChange={(e) => setNote(e.target.value.slice(0, 120))} placeholder="Note (optional), e.g. Family trip" className="h-9" data-testid={`${testIdPrefix}-note`} />
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={submit} disabled={busy} data-testid={`${testIdPrefix}-add-btn`}>Block out</Button>
            <Button variant="ghost" size="sm" onClick={clear} disabled={busy} data-testid={`${testIdPrefix}-clear-btn`}>Clear</Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2" data-testid={`${testIdPrefix}-list`}>
        {sorted.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No dates blocked out.</p>
        ) : sorted.map((b) => (
          <div key={b.id} className={`flex items-center gap-2 bg-slate-50 dark:bg-[#34373c] rounded-lg px-3 py-2 ${b.end_date < todayYmd ? 'opacity-60' : ''}`} data-testid={`${testIdPrefix}-row-${b.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{fmtBlockoutRange(b)}</p>
              {(b.note || b.added_by_name) && (
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {[b.note, b.added_by_name ? `added by ${b.added_by_name}` : null].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => setPendingRemove(b)} disabled={busy} aria-label="Remove blockout" data-testid={`${testIdPrefix}-delete-${b.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <AlertDialog open={!!pendingRemove} onOpenChange={(o) => { if (!o) setPendingRemove(null); }}>
        <AlertDialogContent data-testid={`${testIdPrefix}-remove-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove blockout?</AlertDialogTitle>
            <AlertDialogDescription data-testid={`${testIdPrefix}-remove-message`}>
              {pendingRemove ? [fmtBlockoutRange(pendingRemove), pendingRemove.note].filter(Boolean).join(' \u00b7 ') : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-remove-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { const b = pendingRemove; setPendingRemove(null); if (b) onDelete(b); }}
              data-testid={`${testIdPrefix}-remove-confirm`}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
